import React, { useState } from 'react';
import AppHeader from '../components/layout/AppHeader';

const faqs = [
    {
        q: 'How do I reset my password?',
        a: 'Go to the Login page and click "Forgot Password". You will receive an email with a reset link within 2 minutes. Check your spam folder if you don\'t see it.',
    },
    {
        q: 'How do I get paid?',
        a: 'TechUnity Global supports multiple payment methods including mobile money, bank transfer, and cryptocurrency. Set up your payment method in Settings → Financial → Payment Methods.',
    },
    {
        q: 'How does the Dispatch system work?',
        a: 'Dispatch is our task marketplace. Clients post tasks, and platform members can apply. Once accepted, you complete the work and get paid securely through the platform.',
    },
    {
        q: 'How do I verify my account?',
        a: 'After registration, we send a verification email to your address. Click the link inside to verify. For enhanced trust, you can also complete employer verification in your Settings.',
    },
    {
        q: 'Can I use TechUnity from any country?',
        a: 'Yes! TechUnity Global is accessible from anywhere in the world. We support cross-border payments to ensure you can earn and get paid regardless of your location.',
    },
    {
        q: 'How do I delete my account?',
        a: 'Go to Settings → Account → Delete Account. This action is permanent. Per our Privacy Policy, your data is removed within 30 days of deletion, except financial records retained as required by law.',
    },
    {
        q: 'What is the Academy?',
        a: 'The TechUnity Academy is a curated library of free and premium courses from top providers like Coursera, edX, and more — all in one place. Use it to upskill and qualify for higher-paying Dispatch tasks.',
    },
    {
        q: 'How do I report a user or task?',
        a: 'On any task or profile page, click the ⋯ menu and select "Report". Our moderation team reviews all reports within 24 hours.',
    },
];

const categories = [
    { icon: '🔐', title: 'Account & Login', description: 'Password resets, verification, MFA', href: 'mailto:support@techunityglobal.com?subject=Account Help' },
    { icon: '💳', title: 'Payments & Billing', description: 'Deposits, withdrawals, invoices', href: 'mailto:support@techunityglobal.com?subject=Payment Help' },
    { icon: '📋', title: 'Dispatch & Tasks', description: 'Posting, applying, disputes', href: 'mailto:support@techunityglobal.com?subject=Dispatch Help' },
    { icon: '🎓', title: 'Academy & Courses', description: 'Course access, progress, certificates', href: 'mailto:support@techunityglobal.com?subject=Academy Help' },
    { icon: '🛡️', title: 'Trust & Safety', description: 'Report abuse, account security', href: 'mailto:support@techunityglobal.com?subject=Safety Report' },
    { icon: '⚙️', title: 'Technical Issues', description: 'Bugs, performance, integrations', href: 'mailto:support@techunityglobal.com?subject=Technical Issue' },
];

const Support: React.FC = () => {
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const [search, setSearch] = useState('');

    const filteredFaqs = faqs.filter(f =>
        f.q.toLowerCase().includes(search.toLowerCase()) ||
        f.a.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-premium-wood-dark text-white font-sans">
            <AppHeader title="Support" />

            <main className="container mx-auto px-4 py-12 max-w-5xl">
                {/* Hero */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 bg-premium-gold/10 border border-premium-gold/30 rounded-full px-4 py-1.5 text-premium-gold text-sm font-medium mb-6">
                        💬 Support Center
                    </div>
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
                        How can we <span className="text-premium-gold">help you?</span>
                    </h1>
                    <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
                        Search our knowledge base, browse by category, or contact our team directly.
                    </p>

                    {/* Search */}
                    <div className="relative max-w-xl mx-auto">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-lg">🔍</span>
                        <input
                            type="text"
                            id="support-search"
                            placeholder="Search for help..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white placeholder-white/30 focus:outline-none focus:border-premium-gold transition-colors text-lg"
                        />
                    </div>
                </div>

                {/* Categories */}
                {!search && (
                    <div className="mb-16">
                        <h2 className="text-xl font-bold text-white mb-6">Browse by Category</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categories.map((cat) => (
                                <a
                                    key={cat.title}
                                    href={cat.href}
                                    className="group bg-black/20 hover:bg-black/40 border border-white/5 hover:border-premium-gold/30 rounded-xl p-6 transition-all duration-200"
                                >
                                    <div className="text-3xl mb-3">{cat.icon}</div>
                                    <h3 className="font-bold text-white group-hover:text-premium-gold transition-colors mb-1">{cat.title}</h3>
                                    <p className="text-white/50 text-sm">{cat.description}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* FAQ */}
                <div className="mb-16">
                    <h2 className="text-xl font-bold text-white mb-6">
                        {search ? `Results for "${search}"` : 'Frequently Asked Questions'}
                    </h2>

                    {filteredFaqs.length === 0 ? (
                        <div className="text-center py-12 text-white/50">
                            <p className="text-4xl mb-4">🤔</p>
                            <p>No results found. Try a different search or <a href="mailto:support@techunityglobal.com" className="text-premium-gold hover:underline">contact us directly</a>.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredFaqs.map((faq, i) => (
                                <div
                                    key={i}
                                    className="bg-black/20 border border-white/5 rounded-xl overflow-hidden"
                                >
                                    <button
                                        id={`faq-${i}`}
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors"
                                    >
                                        <span className="font-medium text-white">{faq.q}</span>
                                        <span className={`text-premium-gold shrink-0 text-xl transition-transform duration-200 ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                                    </button>
                                    {openFaq === i && (
                                        <div className="px-6 pb-5 text-white/70 text-sm leading-relaxed border-t border-white/5 pt-4">
                                            {faq.a}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Direct Contact CTA */}
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-premium-gold/10 to-transparent border border-premium-gold/20 rounded-xl p-8">
                        <div className="text-3xl mb-4">✉️</div>
                        <h3 className="font-bold text-white text-xl mb-2">Email Support</h3>
                        <p className="text-white/60 text-sm mb-4">Get a response within 24 hours on business days.</p>
                        <a
                            href="mailto:support@techunityglobal.com"
                            className="inline-block bg-premium-gold text-premium-wood-dark font-bold px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity text-sm"
                        >
                            support@techunityglobal.com
                        </a>
                    </div>

                    <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-8">
                        <div className="text-3xl mb-4">💬</div>
                        <h3 className="font-bold text-white text-xl mb-2">Privacy Questions</h3>
                        <p className="text-white/60 text-sm mb-4">For data requests, deletions, or GDPR/privacy inquiries.</p>
                        <a
                            href="mailto:privacy@techunityglobal.com"
                            className="inline-block border border-premium-gold/30 text-premium-gold font-bold px-5 py-2.5 rounded-lg hover:bg-premium-gold/10 transition-colors text-sm"
                        >
                            privacy@techunityglobal.com
                        </a>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Support;
